import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { LIST_TYPE_BULLET, ListChange } from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'

// Maps schema mark names to the suffix used in EDITOR.TRACK_CHANGES.FORMAT_<KEY> /
// FORMAT_NOT_<KEY> i18n keys. Adding a new tracked formatting mark requires adding
// an entry here AND the corresponding translation keys in every locale.
const MARK_NAME_TO_DISPLAY_KEY: Record<string, string> = {
  strong: 'BOLD',
  italic: 'ITALIC',
}

export type SuggestionTextRun = {
  text: string
  marks: string[]
}

@Component({
  selector: 'app-suggestion-popup',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './suggestion-popup.component.html',
  styleUrl: './suggestion-popup.component.scss',
})
export class SuggestionPopupComponent implements OnChanges {
  @Input({ required: true })
  suggestionId!: string

  @Input({ required: true })
  authorName!: string

  @Input({ required: true })
  insertionRuns!: SuggestionTextRun[]

  @Input({ required: true })
  deletionRuns!: SuggestionTextRun[]

  @Input({ required: true })
  formattingAdded!: string[]

  @Input({ required: true })
  formattingRemoved!: string[]

  @Input({ required: true })
  listChanges!: ListChange[]

  @Input({ required: true })
  canResolve!: boolean

  @Output()
  accept = new EventEmitter<string>()

  @Output()
  reject = new EventEmitter<string>()

  formattingLabels: string[] = []
  listChangeLabels: string[] = []

  private translateService = inject(TranslateService)

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formattingAdded'] !== undefined || changes['formattingRemoved'] !== undefined) {
      this.formattingLabels = [
        ...this.formattingAdded.map((markName) => this.lookupFormatLabel(markName, false)),
        ...this.formattingRemoved.map((markName) => this.lookupFormatLabel(markName, true)),
      ].filter((label): label is string => label !== null)
    }
    if (changes['listChanges'] !== undefined) {
      this.listChangeLabels = this.listChanges.map((change) => this.lookupListChangeLabel(change)).filter((label): label is string => label !== null)
    }
  }

  get hasInsertion(): boolean {
    return this.insertionRuns.length > 0
  }

  get hasDeletion(): boolean {
    return this.deletionRuns.length > 0
  }

  get hasFormatting(): boolean {
    return this.formattingLabels.length > 0
  }

  get hasListChanges(): boolean {
    return this.listChangeLabels.length > 0
  }

  onAccept() {
    this.accept.emit(this.suggestionId)
  }

  onReject() {
    this.reject.emit(this.suggestionId)
  }

  private lookupFormatLabel(markName: string, negated: boolean): string | null {
    const displayKey = MARK_NAME_TO_DISPLAY_KEY[markName] ?? null
    if (displayKey === null) {
      return null
    }
    const i18nKey = negated ? `EDITOR.TRACK_CHANGES.FORMAT_NOT_${displayKey}` : `EDITOR.TRACK_CHANGES.FORMAT_${displayKey}`
    return this.translateService.instant(i18nKey)
  }

  private lookupListChangeLabel(change: ListChange): string | null {
    if (change.kind === 'wrap') {
      const key = change.listType === LIST_TYPE_BULLET ? 'LIST_WRAPPED_BULLET' : 'LIST_WRAPPED_NUMBERED'
      return this.translateService.instant(`EDITOR.TRACK_CHANGES.${key}`)
    }
    if (change.kind === 'unwrap') {
      const key = change.listType === LIST_TYPE_BULLET ? 'LIST_UNWRAPPED_BULLET' : 'LIST_UNWRAPPED_NUMBERED'
      return this.translateService.instant(`EDITOR.TRACK_CHANGES.${key}`)
    }
    if (change.kind === 'changeListType') {
      const key = change.to === LIST_TYPE_BULLET ? 'LIST_TYPE_CHANGED_TO_BULLET' : 'LIST_TYPE_CHANGED_TO_NUMBERED'
      return this.translateService.instant(`EDITOR.TRACK_CHANGES.${key}`)
    }
    if (change.delta === 0) {
      return null
    }
    const count = Math.abs(change.delta)
    const base = change.delta > 0 ? 'LIST_INDENTED' : 'LIST_OUTDENTED'
    const key = count === 1 ? base : `${base}_PLURAL`
    return this.translateService.instant(`EDITOR.TRACK_CHANGES.${key}`, { count })
  }
}
